import {
  DESCRIPTION_IRIS,
  DESIGNER_COLOR_ID_IRI,
  DESIGNER_EXISTS_IRI,
  LABEL_IRIS,
} from 'src/templates/queries/iris';

/**
 * Determines the labels and descriptions for a set of IRIs with a set of label predicates.
 *
 * This query is used to provide human-readable labels and descriptions across the application.
 *
 * **Call Frequency**
 *
 * For project models, labels and descriptions are captured once when the model is added to a project.
 *
 * **Context**
 * - Named graphs: set by the user (default is `reasoning.schema.graphs` value,
 *   which by default is `tag:stardog:api:context:local`)
 * - Reasoning: always off
 *
 * **Example**
 * ```sparql
# Labels and Descriptions
CONSTRUCT {
  ?subject ?labelPredicate ?label .
  ?subject ?descriptionPredicate ?description .
  ?subject <tag:stardog:designer:colorId> ?colorId .
  ?subject <tag:stardog:designer:search:exists> true
}
WHERE {
  VALUES (?subject) {
    (<http://api.stardog.com/Alice>) (<http://api.stardog.com/ACME>) (<http://api.stardog.com/CEO>)
  }
  VALUES (?labelPredicate) {
    (<http://www.w3.org/2000/01/rdf-schema#label>) (<http://www.w3.org/2004/02/skos/core#prefLabel>)
  }
  VALUES (?descriptionPredicate) {
    (<http://www.w3.org/2000/01/rdf-schema#label>) (<http://www.w3.org/2004/02/skos/core#prefLabel>)
  }
  OPTIONAL {
    ?subject ?labelPredicate ?label
  }
  OPTIONAL {
    ?subject ?descriptionPredicate ?description
  }
  OPTIONAL {
    ?subject <tag:stardog:designer:colorId> ?colorId
  }
}
 * ```
 */
export const buildLabelsAndDescriptionsQuery = (
  iris: string[]
) => `# Labels and Descriptions
CONSTRUCT {
  ?subject ?labelPredicate ?label .
  ?subject ?descriptionPredicate ?description .
  ?subject <${DESIGNER_COLOR_ID_IRI}> ?colorId .
  ?subject <${DESIGNER_EXISTS_IRI}> true
}
WHERE {
  VALUES (?subject) {
    ${iris.map((iri) => `(<${iri}>)`).join(' ')}
  }
  VALUES (?labelPredicate) {
    ${LABEL_IRIS.map((iri) => `(<${iri}>)`).join(' ')}
  }
  VALUES (?descriptionPredicate) {
    ${DESCRIPTION_IRIS.map((iri) => `(<${iri}>)`).join(' ')}
  }
  OPTIONAL {
    ?subject ?labelPredicate ?label
  }
  OPTIONAL {
    ?subject ?descriptionPredicate ?description
  }
  OPTIONAL {
    ?subject <${DESIGNER_COLOR_ID_IRI}> ?colorId
  }
}`;
